import { useState } from 'react'

import {
  Button,
  Card,
  DropdownItem,
  LoadingIndicator,
  Modal,
  Typography,
} from 'components/common'
import { addToast } from 'components/features/Other/api'
import { Form, Formik } from 'formik'
import { useAppDispatch, useToggle } from 'hooks'
import { t } from 'i18next'
import { Edit2, Edit3 } from 'react-feather'
import * as yup from 'yup'

import styles from './MaterialChangeNameModal.module.scss'
import { FTextField } from '../../../../formik-factory'
import { materialsApi } from '../../api/materialsApi'

const MaterialChangeNameModal = ({
  materialId,
  togglePopover,
  onMaterialRename,
}: {
  materialId: string
  togglePopover: () => void
  onMaterialRename: (newName: string) => void
}): JSX.Element => {
  const [isModalOpen, toggleModal] = useToggle()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const dispatch = useAppDispatch()

  const [updateMaterial] = materialsApi.useUpdateMaterialMutation()

  const newNameValidationSchema = {
    newName: yup.string().max(32).min(3).required().trim(),
  }

  const handleClose = () => {
    if (isSubmitting) return false

    toggleModal(false)
    togglePopover()
  }

  const handleRenameMaterial = async (newName: string) => {
    if (!materialId) return

    try {
      setIsSubmitting(true)

      await updateMaterial({ id: materialId, body: { name: newName } })
      dispatch(
        addToast({
          color: 'success',
          content: t('materials.popover.changeMaterialNameSuccess'),
        })
      )

      onMaterialRename(newName)
    } catch (err) {
      dispatch(addToast({ color: 'error', content: t('error.general') }))
    } finally {
      setIsSubmitting(false)
      handleClose()
    }
  }

  return (
    <>
      <Modal open={isModalOpen} handleClose={handleClose}>
        <Card className={styles.card}>
          <Formik
            onSubmit={(values) => {
              handleRenameMaterial(values.newName)
            }}
            initialValues={{ newName: '' }}
            validationSchema={yup.object().shape(newNameValidationSchema)}
          >
            {({ values, handleChange, handleSubmit, isSubmitting }) => (
              <>
                <Card.Header className={styles.header}>
                  <Typography
                    element={'h2'}
                    size={'x-large'}
                    fontWeight={'bold'}
                    className={styles.heading}
                  >
                    {t('materials.popover.changeName')}
                  </Typography>
                  <Typography>
                    {t('materials.popover.changeNameHeader')}
                  </Typography>
                </Card.Header>
                <Form onSubmit={handleSubmit}>
                  <FTextField
                    className={styles.input}
                    value={values.newName}
                    onChange={handleChange}
                    id={'newName'}
                    name={'newName'}
                    label={'New Name'}
                  />
                  <Card.Footer className={styles.footer}>
                    <Button
                      size={'small'}
                      variant="text"
                      type={'button'}
                      disabled={isSubmitting}
                      onClick={handleClose}
                    >
                      {t('document.cancelDelete')}
                    </Button>
                    <Button
                      disabled={isSubmitting}
                      type={'submit'}
                      size={'small'}
                      icon={
                        isSubmitting ? (
                          <LoadingIndicator size="x-small" />
                        ) : (
                          <Edit3 />
                        )
                      }
                    >
                      Change
                    </Button>
                  </Card.Footer>
                </Form>
              </>
            )}
          </Formik>
        </Card>
      </Modal>
      <DropdownItem
        className={styles.link}
        icon={<Edit2 />}
        onClick={() => toggleModal(true)}
      >
        <Typography>{t('materials.popover.changeName')}</Typography>
      </DropdownItem>
    </>
  )
}

export default MaterialChangeNameModal
